import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { normalizeApiData } from '../utils/apiHelpers';

interface Role {
  id: number;
  nome: string;
  descricao: string;
  permissaos_id: number;
  paginaList: string[];
  users: any[];
}

// Mapeamento de rotas para nomes de páginas
const routeToPageMap: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/clients': 'clients',
  '/suppliers': 'suppliers',
  '/boxes': 'boxes',
  '/invoices': 'invoices',
  '/labels': 'labels',
  '/rastreamento': 'rastreamento',
  '/tracking': 'tracking',
  '/costs': 'costs',
  '/documents': 'documents',
  '/users': 'users',
  '/settings': 'settings',
  '/profile': 'profile',
};

export const usePermissions = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<Role | null>(null);
  const [allowedPages, setAllowedPages] = useState<string[]>([]);
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  // Carregar roles da API
  const loadRoles = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getRoles();
      const rolesList = normalizeApiData(data);
      const normalizedRoles = Array.isArray(rolesList) ? rolesList : [];
      setRoles(normalizedRoles);

      // Se não encontrou roles, logar para debug
      if (normalizedRoles.length === 0) {
        console.warn('Nenhum role encontrado na resposta da API:', data);
      }
    } catch (error) {
      console.error('Erro ao carregar roles:', error);
      setRoles([]);
      // Garantir que loading seja false mesmo em caso de erro
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Encontrar o role do usuário baseado no permissaos_id
  const findUserRole = useCallback(() => {
    if (!user || !roles.length) {
      return null;
    }

    // Obter permissaos_id do usuário (pode estar em diferentes campos)
    const userPermissaoId = user.permissaos_id || user.permissao?.id;

    if (!userPermissaoId) {
      console.warn('Usuário não tem permissaos_id definido');
      return null;
    }

    // Converter para número para comparação
    const userPermissaoIdNum = Number(userPermissaoId);

    // Encontrar role que corresponde ao permissaos_id do usuário
    const role = roles.find(r => {
      const rolePermissaoIdNum = Number(r.permissaos_id);
      return rolePermissaoIdNum === userPermissaoIdNum;
    });

    if (!role) {
      console.warn('Role não encontrado para permissaos_id:', userPermissaoIdNum);
    }

    return role || null;
  }, [user, roles]);

  // Verificar se o usuário tem acesso a uma página específica
  const hasAccessToPage = useCallback((pageName: string): boolean => {
    // Profile sempre deve ser acessível para usuários autenticados (página do próprio usuário)
    if (pageName === 'profile') {
      return true;
    }

    if (!userRole) {
      return false;
    }

    return userRole.paginaList.includes(pageName);
  }, [userRole]);

  // Verificar se o usuário tem acesso à rota atual
  const hasAccessToCurrentRoute = useCallback((): boolean => {
    const currentPage = routeToPageMap[location.pathname];

    if (!currentPage) {
      // Se a rota não está mapeada, permitir acesso (pode ser uma rota pública ou especial)
      return true;
    }

    return hasAccessToPage(currentPage);
  }, [location.pathname, hasAccessToPage]);

  // Carregar roles quando o componente monta ou quando o usuário muda
  useEffect(() => {
    // Aguardar o AuthContext terminar de carregar
    if (authLoading) {
      return;
    }

    if (user) {
      loadRoles();
    } else {
      // Se não há usuário, definir loading como false
      setLoading(false);
      setUserRole(null);
      setAllowedPages([]);
    }
  }, [user, authLoading, loadRoles]);

  // Timeout de segurança: se loading não terminar em 10 segundos, forçar false
  useEffect(() => {
    if (loading && user && !authLoading) {
      const timeout = setTimeout(() => {
        console.warn('Timeout ao carregar permissões. Forçando loading = false.');
        setLoading(false);
      }, 10000);

      return () => clearTimeout(timeout);
    }
  }, [loading, user, authLoading]);

  // Atualizar userRole quando roles ou user mudarem
  useEffect(() => {
    const role = findUserRole();
    setUserRole(role);

    if (role) {
      setAllowedPages(role.paginaList);
    } else {
      setAllowedPages([]);
    }
  }, [findUserRole]);

  // Obter a primeira página permitida para o usuário (para redirecionamento após login)
  const getFirstAllowedPage = useCallback((): string => {
    // Se não tem role ou páginas permitidas, retornar profile como fallback seguro
    if (!userRole || !allowedPages.length) {
      console.warn('Nenhuma página permitida encontrada. Redirecionando para /profile');
      return '/profile';
    }

    // Ordem de prioridade das páginas (de cima para baixo)
    const pagePriority = [
      'dashboard',
      'clients',
      'suppliers',
      'boxes',
      'invoices',
      'labels',
      'tracking',
      'costs',
      'documents',
      'users',
      'settings',
    ];

    // Encontrar a primeira página permitida na ordem de prioridade
    for (const page of pagePriority) {
      if (allowedPages.includes(page)) {
        const route = Object.keys(routeToPageMap).find(
          key => routeToPageMap[key] === page
        );
        if (route) {
          return route;
        }
      }
    }

    // Se nenhuma página foi encontrada na lista de prioridade, mas há páginas permitidas,
    // retornar a primeira página permitida disponível
    if (allowedPages.length > 0) {
      const firstAllowedPage = allowedPages[0];
      const route = Object.keys(routeToPageMap).find(
        key => routeToPageMap[key] === firstAllowedPage
      );
      if (route) {
        return route;
      }
    }

    // Último recurso: retornar profile (sempre acessível)
    return '/profile';
  }, [userRole, allowedPages]);

  return {
    roles,
    userRole,
    allowedPages,
    loading: loading || authLoading,
    hasAccessToPage,
    hasAccessToCurrentRoute,
    getFirstAllowedPage,
    loadRoles,
  };
};
